import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, ElementRef, ViewChild } from '@angular/core';
import { BaseService } from '../../../../../services/base.service';
import { TicketService } from '../../../../../services/ticket.service';
import { DatePipe } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TicketMessage } from './ticket.interface';
import { MatDialog } from '@angular/material/dialog';
import { ATTACHMENTS_TYPES, UPLOAD_FILE_TYPES } from '../../../../../enum/constants';
import { FileUploadNewComponent } from '../../../../../modals/file-upload-new/file-upload-new.component';
import { ModalService } from '../../../../../services/modal.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-ticket',
  templateUrl: './ticket.component.html',
  styleUrls: ['./ticket.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TicketComponent implements OnInit {
  @ViewChild('textArea') private readonly textArea?: ElementRef<HTMLTextAreaElement>;
  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  public ticketForm!: FormGroup<TicketMessage>;

  public ticket!: any;
  public messages: any[] = [];
  public title!: string;
  public isLoader: boolean = false;
  public ticketFiles: any[] = [];

  constructor(
    readonly baseService: BaseService,
    public readonly ticketService: TicketService,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    private readonly modalService: ModalService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit() {
    this.ticket = this.payload?.data?.ticket;
    this.title = this.ticket.name;

    this.initForm();
    this.initMessages();
  }

  public initForm() {
    this.ticketForm = new FormGroup<TicketMessage>({
      text: new FormControl(null, [Validators.required]),
    });
  }

  public initMessages() {
    this.messages.splice(0, this.messages.length);
    this.isLoader = true;
    this.ticketService.getTicket(this.ticket.id).subscribe((res: any) => {
      this.ticket = res;
    });

    const params = {
      take: 25,
      page: 1,
      search: '',
      id: this.ticket.id,
    };

    this.ticketService.getMessageTicket(params).subscribe((res: any) => {
      if (res.items) {
        this.messages.push(...res.items);
      }
      this.isLoader = false;
    });
  }

  closeBar() {
    this.close.emit();
  }

  formatDate(date: string): string {
    return this.datePipe.transform(date, 'dd.MM.yyyy HH:mm') ?? '';
  }

  sendMessage() {
    this.commitComposition();

    if (this.ticketForm.valid) {
      const payloadForm = this.ticketForm.getRawValue();

      let attachment = null;

      if (this.ticketFiles.length) {
        attachment = this.ticketFiles;
      }

      const payload = {
        text: payloadForm.text,
        ticket_id: this.ticket.id,
        attachment: attachment,
      };

      this.ticketService.sendMessage(payload).subscribe({
        next: () => {
          this.initMessages();
          this.ticketForm.reset();
          this.ticketFiles.splice(0, this.ticketFiles.length);
        },
      });
    }
  }

  /**
   * Android keyboards keep shortcut/autocorrect text as an open composition; blur + refocus makes them
   * commit it before the form value is read and later reset (otherwise the input desyncs and freezes).
   */
  private commitComposition(): void {
    const el = this.textArea?.nativeElement;
    if (!el || document.activeElement !== el) return;

    el.blur();
    el.focus({ preventScroll: true });
  }

  /** Keys that confirm OS text replacement / IME input must not be intercepted (see chat input). */
  private isComposing(e: Event): boolean {
    return (e as KeyboardEvent).isComposing || (e as KeyboardEvent).keyCode === 229;
  }

  public pressCmdEnter(e: Event): void {
    if (this.isComposing(e)) return;

    if (this.baseService.currentUserInfo.settings.hot_key_send_chat === 'cmd_enter' && !this.baseService.isMobile) {
      this.sendMessage();
    }
  }

  public pressShiftEnter(e: Event): void {
    if (this.isComposing(e)) return;

    if (this.baseService.currentUserInfo.settings.hot_key_send_chat === 'shift_enter' && !this.baseService.isMobile) {
      this.sendMessage();
    }
  }

  public pressEnter(e: Event): void {
    if (this.isComposing(e)) return;

    if (this.baseService.currentUserInfo.settings.hot_key_send_chat === 'enter' && !this.baseService.isMobile) {
      e.preventDefault();
      this.sendMessage();
    }
  }

  openFileUpload() {
    const maxUploadFiles = UPLOAD_FILE_TYPES['TICKET'].max_upload_files;
    const allowedMediaTypes = UPLOAD_FILE_TYPES['TICKET'].allowed_media_types;

    if (maxUploadFiles && this.ticketFiles.length >= maxUploadFiles) {
      this.notificationService.showWarning(
        this.translate.instant('notifications_texts.warn.warn'),
        this.translate.instant('notifications_texts.warn.no_more_upload_files', { limit: maxUploadFiles }),
      );
      return;
    }

    this.modalService
      .openModal({
        translate: true,
        component: FileUploadNewComponent,
        title: 'upload_file',
        height: 'auto',
        data: {
          length: this.ticketFiles.length,
          max_upload_files: maxUploadFiles,
          allowed_media_types: allowedMediaTypes,
        },
      })
      .subscribe((res: any[]) => {
        if (res) {
          this.ticketFiles.push(
            ...res.map((v) => ({
              url: v.link,
              mimeType: v.mimeType,
              type: ATTACHMENTS_TYPES.IMAGE,
              name: v.name,
              size: v.size,
            })),
          );
        }
      });
  }

  deletePhoto(index: number) {
    this.ticketFiles.splice(index, 1);
  }
}
